import type { FilterOption } from '@/components/UI/Table/TableComponent.model.ts';
import { FilterTypes as filterType } from '@/constants/FilterTypes.ts';
import { _isNotEmpty, _notNil } from '@/littledash.ts';
import type { ID } from '@/model/Common.model.ts';
import type { StudyGroupV1 } from '@/model/TreatmentGroup.model.ts';
import type { components } from '@/generated/internal-api/openapi-types';
import { type SampleDefinitionV1 } from '@/model/SamplePlan.model.ts';
import type { TableFilter } from '@/components/UI/Table/Reusable/APITable/APITable.model.ts';

export type AnimalForSampling = components['schemas']['SampleExecutionAnimalV1.schema'];

export const createDefaultLivingStatusFilter = (): TableFilter => ({
  category: {
    name: 'Living status',
    value: 'deceased',
    index: 0,
  },
  operation: {
    name: filterType.eq,
    index: 0,
  },
  option: {
    name: 'Alive',
    value: 'null',
  },
});

export const animalFilterOptions = (
  animals: Array<AnimalForSampling> | undefined = undefined,
  studyId?: ID
): Array<FilterOption> => {
  const studyGroupsMap = new Map<number, StudyGroupV1>();

  animals?.forEach((animal) => {
    // Collect study groups
    if (_notNil(animal.study_group) && _notNil(animal.study_group.id)) {
      studyGroupsMap.set(animal.study_group.id, animal.study_group);
    }
  });

  const studyGroups = Array.from(studyGroupsMap.values());

  const filters: Array<FilterOption> = [
    {
      value: 'cage',
      name: 'Cage name',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: [],
        },
        {
          name: 'is not equal to',
          value: filterType.ne,
          options: [],
        },
        {
          name: 'contains',
          value: filterType.contains,
        },
        {
          name: 'does not contain',
          value: filterType.not_contains,
        },
      ],
      ...(_notNil(studyId) && {
        fetchFromApi: {
          placeholder: 'Search cages',
          url: 'studies.cages.list',
          params: {
            studyId,
          },
          queryParams: {
            sort: 'catalog',
            order: 'asc',
            perPage: -1,
          },
        },
      }),
    },
    {
      value: 'deceased',
      name: 'Living status',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: [{ name: 'Alive', value: 'null' }],
        },
        {
          name: 'is not equal to',
          value: filterType.ne,
          options: [{ name: 'Alive', value: 'null' }],
        },
      ],
    },
    {
      value: 'sex',
      name: 'Sex',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: [
            { value: 'm', name: 'Male' },
            { value: 'f', name: 'Female' },
            { value: 'u', name: 'Unknown' },
          ],
        },
      ],
    },
  ];

  // Add study group filters if provided
  if (_isNotEmpty(studyGroups)) {
    filters.push({
      value: 'study_group',
      name: 'Study Group',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: [],
        },
        {
          name: 'is not equal to',
          value: filterType.ne,
          options: [],
        },
        {
          name: filterType.IS_NULL,
          value: filterType.IS_NULL,
        },
        {
          name: filterType.IS_NOT_NULL,
          value: filterType.IS_NOT_NULL,
        },
      ],
      ...(_notNil(studyId)
        ? {
            fetchFromApi: {
              placeholder: 'Search groups',
              url: 'studies.groups.list',
              params: { id: studyId },
              queryParams: { perPage: -1 },
            },
          }
        : {}),
    });
  }

  return filters;
};

export const calculateNumberOfSamples = (sampleDefinitions: SampleDefinitionV1[]): number => {
  return sampleDefinitions.reduce((totalSamples, sampleDefinition) => {
    const timepointsCount = sampleDefinition.timepoints?.length || 0;

    // If there are no items or just 1 item in the timepoints array, count as 1 sample
    // If there are 2+ timepoints, count as the number of timepoints
    const samplesForThisDefinition = timepointsCount <= 1 ? 1 : timepointsCount;

    return totalSamples + samplesForThisDefinition;
  }, 0);
};
